import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, RotateCcw, Play, Info, Star,
  Cog, Scale, ArrowDown, ArrowUp, HelpCircle,
  Minus, Plus, Move, Triangle
} from 'lucide-react';

const SimpleMachinesPage = () => {
  const navigate = useNavigate();
  const [currentExperiment, setCurrentExperiment] = useState('lever');
  const [score, setScore] = useState(0);
  const [showHint, setShowHint] = useState(false);

  // Lever State
  const [leftWeight, setLeftWeight] = useState(50);
  const [rightWeight, setRightWeight] = useState(50);
  const [leftDistance, setLeftDistance] = useState(100);
  const [rightDistance, setRightDistance] = useState(100);
  const [leverAngle, setLeverAngle] = useState(0);
  const [isBalanced, setIsBalanced] = useState(true);

  // Pulley State
  const [loadWeight, setLoadWeight] = useState(100);
  const [pulleyType, setPulleyType] = useState('single'); // single, double, block
  const [effortNeeded, setEffortNeeded] = useState(100);
  const [pulleyPosition, setPulleyPosition] = useState(50);
  const [isPulling, setIsPulling] = useState(false);

  // Inclined Plane State
  const [planeAngle, setPlaneAngle] = useState(30);
  const [objectMass, setObjectMass] = useState(10);
  const [objectPosition, setObjectPosition] = useState(0);
  const [isSliding, setIsSliding] = useState(false);

  const experiments = [
    {
      id: 'lever',
      title: 'Lever Balance',
      titleHindi: 'उत्तोलक संतुलन',
      description: 'Seesaw balance karo!',
      icon: Scale
    },
    {
      id: 'pulley',
      title: 'Pulley System',
      titleHindi: 'घिरनी तंत्र',
      description: 'Heavy load uthao easily!',
      icon: Cog
    },
    {
      id: 'incline',
      title: 'Inclined Plane',
      titleHindi: 'झुका हुआ तल',
      description: 'Ramp pe physics seekho!',
      icon: Triangle
    }
  ];

  // Lever Balance Logic
  useEffect(() => {
    const leftMoment = leftWeight * leftDistance;
    const rightMoment = rightWeight * rightDistance;
    const diff = leftMoment - rightMoment;
    
    // Calculate angle based on moment difference
    const maxAngle = 20;
    const angle = Math.max(-maxAngle, Math.min(maxAngle, diff / 50));
    setLeverAngle(angle);
    setIsBalanced(Math.abs(diff) < 100);
  }, [leftWeight, rightWeight, leftDistance, rightDistance]);

  // Pulley calculation
  useEffect(() => {
    let effort = loadWeight;
    if (pulleyType === 'single') effort = loadWeight;
    else if (pulleyType === 'double') effort = loadWeight / 2;
    else if (pulleyType === 'block') effort = loadWeight / 4;
    setEffortNeeded(effort);
  }, [loadWeight, pulleyType]);

  // Pulley animation
  useEffect(() => {
    if (isPulling && pulleyPosition < 200) {
      const interval = setInterval(() => {
        setPulleyPosition(prev => {
          if (prev >= 200) {
            setIsPulling(false);
            setScore(s => s + 15);
            return 200;
          }
          return prev + 2;
        });
      }, 30);
      return () => clearInterval(interval);
    }
  }, [isPulling]);

  // Inclined plane animation
  useEffect(() => {
    if (isSliding && objectPosition < 100) {
      const interval = setInterval(() => {
        setObjectPosition(prev => {
          if (prev >= 100) {
            setIsSliding(false);
            setScore(s => s + 10);
            return 100;
          }
          return prev + 1;
        });
      }, 50);
      return () => clearInterval(interval);
    }
  }, [isSliding]);

  const resetExperiment = () => {
    setLeverAngle(0);
    setPulleyPosition(50);
    setObjectPosition(0);
    setIsPulling(false);
    setIsSliding(false);
    setLeftWeight(50);
    setRightWeight(50);
    setLeftDistance(100);
    setRightDistance(100);
  };

  // Calculate mechanical advantage for inclined plane
  const mechanicalAdvantage = (1 / Math.sin(planeAngle * Math.PI / 180)).toFixed(2);
  const forceNeeded = ((objectMass * 9.8) / mechanicalAdvantage).toFixed(1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/science')}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-white/20"></div>
              <div>
                <h1 className="text-lg font-bold text-white flex items-center gap-2">
                  <Cog className="w-5 h-5 text-teal-400" />
                  Simple Machines
                  <span className="text-teal-400 text-sm">(सरल मशीनें)</span>
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-500/20 border border-teal-500/30 rounded-full">
                <Star className="w-4 h-4 text-teal-400" />
                <span className="text-teal-300 font-semibold">{score} points</span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={resetExperiment}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Cog className="w-4 h-4 text-purple-400" />
                  Machines
                </h3>
                <div className="space-y-2">
                  {experiments.map((exp) => (
                    <button
                      key={exp.id}
                      onClick={() => {
                        setCurrentExperiment(exp.id);
                        resetExperiment();
                      }}
                      className={`w-full p-3 rounded-xl text-left transition-all ${
                        currentExperiment === exp.id 
                          ? 'bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-500/50' 
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <exp.icon className={`w-5 h-5 ${currentExperiment === exp.id ? 'text-purple-400' : 'text-white/60'}`} />
                        <div>
                          <p className={`font-medium ${currentExperiment === exp.id ? 'text-white' : 'text-white/80'}`}>
                            {exp.title}
                          </p>
                          <p className="text-xs text-pink-300">{exp.titleHindi}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Controls */}
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl">
              <CardContent className="p-4 space-y-4">
                <h4 className="text-white font-semibold">Controls</h4>

                {currentExperiment === 'lever' && (
                  <>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Left Weight</span>
                        <span className="text-red-400">{leftWeight} kg</span>
                      </label>
                      <input 
                        type="range" min="10" max="100" value={leftWeight}
                        onChange={(e) => setLeftWeight(Number(e.target.value))}
                        className="w-full accent-red-500"
                      />
                    </div>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Right Weight</span>
                        <span className="text-blue-400">{rightWeight} kg</span>
                      </label>
                      <input 
                        type="range" min="10" max="100" value={rightWeight}
                        onChange={(e) => setRightWeight(Number(e.target.value))}
                        className="w-full accent-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Left Distance</span>
                        <span className="text-orange-400">{leftDistance} cm</span>
                      </label>
                      <input 
                        type="range" min="50" max="150" value={leftDistance}
                        onChange={(e) => setLeftDistance(Number(e.target.value))}
                        className="w-full accent-orange-500"
                      />
                    </div>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Right Distance</span>
                        <span className="text-green-400">{rightDistance} cm</span>
                      </label>
                      <input 
                        type="range" min="50" max="150" value={rightDistance}
                        onChange={(e) => setRightDistance(Number(e.target.value))}
                        className="w-full accent-green-500"
                      />
                    </div>
                  </>
                )}

                {currentExperiment === 'pulley' && (
                  <>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Load Weight</span>
                        <span className="text-yellow-400">{loadWeight} kg</span>
                      </label>
                      <input 
                        type="range" min="50" max="200" value={loadWeight}
                        onChange={(e) => setLoadWeight(Number(e.target.value))}
                        className="w-full accent-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="text-white/80 text-sm mb-2 block">Pulley Type</label>
                      <div className="grid grid-cols-3 gap-2">
                        {['single', 'double', 'block'].map((type) => (
                          <button
                            key={type}
                            onClick={() => setPulleyType(type)}
                            className={`p-2 rounded-lg text-xs transition-all ${
                              pulleyType === type 
                                ? 'bg-purple-500 text-white' 
                                : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                          >
                            {type === 'single' ? '1x' : type === 'double' ? '2x' : '4x'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button 
                      onClick={() => {
                        setPulleyPosition(50);
                        setIsPulling(true);
                      }}
                      disabled={isPulling}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                    >
                      <ArrowUp className="w-4 h-4 mr-2" />
                      Pull Rope!
                    </Button>
                  </>
                )}

                {currentExperiment === 'incline' && (
                  <>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Ramp Angle</span>
                        <span className="text-cyan-400">{planeAngle}°</span>
                      </label>
                      <input 
                        type="range" min="10" max="60" value={planeAngle}
                        onChange={(e) => setPlaneAngle(Number(e.target.value))}
                        className="w-full accent-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-white/80 text-sm flex justify-between mb-2">
                        <span>Object Mass</span>
                        <span className="text-pink-400">{objectMass} kg</span>
                      </label>
                      <input 
                        type="range" min="5" max="50" value={objectMass}
                        onChange={(e) => setObjectMass(Number(e.target.value))}
                        className="w-full accent-pink-500"
                      />
                    </div>
                    <Button 
                      onClick={() => {
                        setObjectPosition(0);
                        setIsSliding(true);
                      }}
                      disabled={isSliding}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-500"
                    >
                      <Move className="w-4 h-4 mr-2" />
                      Push Up!
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Area */}
          <div className="lg:col-span-2">
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl h-full">
              <CardContent className="p-6">
                <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl overflow-hidden" style={{ height: '450px' }}>
                  
                  {currentExperiment === 'lever' && (
                    <svg viewBox="0 0 600 350" className="w-full h-full">
                      {/* Background */}
                      <rect width="600" height="350" fill="#1e1b4b"/>
                      
                      {/* Fulcrum (Triangle) */}
                      <polygon 
                        points="300,280 270,330 330,330" 
                        fill="#6b7280"
                        stroke="#9ca3af"
                        strokeWidth="2"
                      />
                      
                      {/* Lever Beam */}
                      <g transform={`rotate(${leverAngle}, 300, 260)`}>
                        <rect 
                          x="100" y="250" 
                          width="400" height="20" 
                          rx="5"
                          fill="#8b5cf6"
                          stroke="#a78bfa"
                          strokeWidth="2"
                        />
                        
                        {/* Left Weight */}
                        <g transform={`translate(${200 - leftDistance/2}, 200)`}>
                          <rect width="40" height="50" rx="5" fill="#ef4444"/>
                          <text x="20" y="30" textAnchor="middle" fill="white" fontSize="12">{leftWeight}</text>
                          <line x1="20" y1="50" x2="20" y2="50" stroke="#ef4444" strokeWidth="3"/>
                        </g>
                        
                        {/* Right Weight */}
                        <g transform={`translate(${360 + rightDistance/2}, 200)`}>
                          <rect width="40" height="50" rx="5" fill="#3b82f6"/>
                          <text x="20" y="30" textAnchor="middle" fill="white" fontSize="12">{rightWeight}</text>
                        </g>
                        
                        {/* Distance markers */}
                        <line x1="200" y1="270" x2="300" y2="270" stroke="#f97316" strokeWidth="2" strokeDasharray="5,5"/>
                        <line x1="300" y1="270" x2="400" y2="270" stroke="#22c55e" strokeWidth="2" strokeDasharray="5,5"/>
                      </g>
                      
                      {/* Balance Indicator */}
                      <g transform="translate(300, 30)">
                        <circle r="25" fill={isBalanced ? "#22c55e" : "#ef4444"} opacity="0.3"/>
                        <circle r="15" fill={isBalanced ? "#22c55e" : "#ef4444"}/>
                        <text y="5" textAnchor="middle" fill="white" fontSize="10">
                          {isBalanced ? '✓' : '✗'}
                        </text>
                      </g>
                      
                      {/* Labels */}
                      <text x="300" y="320" textAnchor="middle" fill="#9ca3af" fontSize="12">
                        Left Moment: {leftWeight * leftDistance} | Right Moment: {rightWeight * rightDistance}
                      </text>
                    </svg>
                  )}

                  {currentExperiment === 'pulley' && (
                    <svg viewBox="0 0 400 400" className="w-full h-full">
                      {/* Background */}
                      <rect width="400" height="400" fill="#1e1b4b"/>
                      
                      {/* Support beam */}
                      <rect x="150" y="20" width="100" height="15" fill="#6b7280"/>
                      
                      {/* Pulley wheel(s) */}
                      {pulleyType === 'single' && (
                        <g transform="translate(200, 50)">
                          <circle r="25" fill="#374151" stroke="#8b5cf6" strokeWidth="3"/>
                          <circle r="5" fill="#8b5cf6"/>
                        </g>
                      )}
                      {pulleyType === 'double' && (
                        <>
                          <g transform="translate(170, 50)">
                            <circle r="20" fill="#374151" stroke="#8b5cf6" strokeWidth="3"/>
                            <circle r="4" fill="#8b5cf6"/>
                          </g>
                          <g transform="translate(230, 50)">
                            <circle r="20" fill="#374151" stroke="#8b5cf6" strokeWidth="3"/>
                            <circle r="4" fill="#8b5cf6"/>
                          </g>
                        </>
                      )}
                      {pulleyType === 'block' && (
                        <>
                          <g transform="translate(160, 50)">
                            <circle r="18" fill="#374151" stroke="#8b5cf6" strokeWidth="2"/>
                          </g>
                          <g transform="translate(200, 50)">
                            <circle r="18" fill="#374151" stroke="#8b5cf6" strokeWidth="2"/>
                          </g>
                          <g transform="translate(240, 50)">
                            <circle r="18" fill="#374151" stroke="#8b5cf6" strokeWidth="2"/>
                          </g>
                          <g transform="translate(180, 120)">
                            <circle r="15" fill="#374151" stroke="#ec4899" strokeWidth="2"/>
                          </g>
                          <g transform="translate(220, 120)">
                            <circle r="15" fill="#374151" stroke="#ec4899" strokeWidth="2"/>
                          </g>
                        </>
                      )}
                      
                      {/* Rope */}
                      <line x1="200" y1="75" x2="200" y2={350 - pulleyPosition} stroke="#fbbf24" strokeWidth="3"/>
                      
                      {/* Load */}
                      <g transform={`translate(170, ${300 - pulleyPosition})`}>
                        <rect width="60" height="60" rx="5" fill="#ef4444"/>
                        <text x="30" y="35" textAnchor="middle" fill="white" fontSize="14">{loadWeight}kg</text>
                      </g>
                      
                      {/* Person pulling */}
                      <g transform="translate(320, 250)">
                        <circle r="15" fill="#fbbf24"/>
                        <line x1="0" y1="15" x2="0" y2="50" stroke="#fbbf24" strokeWidth="4"/>
                        <line x1="0" y1="25" x2={isPulling ? -30 : -20} y2="40" stroke="#fbbf24" strokeWidth="3"/>
                        <line x1="0" y1="50" x2="-10" y2="80" stroke="#fbbf24" strokeWidth="3"/>
                        <line x1="0" y1="50" x2="10" y2="80" stroke="#fbbf24" strokeWidth="3"/>
                        {/* Rope to person */}
                        <line x1={isPulling ? -30 : -20} y1="40" x2="-100" y2={100 + pulleyPosition/2} stroke="#fbbf24" strokeWidth="2"/>
                      </g>
                      
                      {/* Effort label */}
                      <text x="320" y="350" textAnchor="middle" fill="#22c55e" fontSize="14">
                        Effort: {effortNeeded.toFixed(0)} kg
                      </text>
                      
                      {/* MA label */}
                      <text x="200" y="380" textAnchor="middle" fill="#a78bfa" fontSize="12">
                        Mechanical Advantage: {pulleyType === 'single' ? '1x' : pulleyType === 'double' ? '2x' : '4x'}
                      </text>
                    </svg>
                  )}

                  {currentExperiment === 'incline' && (
                    <svg viewBox="0 0 500 350" className="w-full h-full">
                      {/* Background */}
                      <rect width="500" height="350" fill="#1e1b4b"/>
                      
                      {/* Ground */}
                      <rect x="0" y="300" width="500" height="50" fill="#374151"/>
                      
                      {/* Inclined Plane */}
                      <g>
                        <polygon 
                          points={`50,300 ${50 + 300},300 50,${300 - 300 * Math.tan(planeAngle * Math.PI / 180)}`}
                          fill="#6366f1"
                          stroke="#818cf8"
                          strokeWidth="2"
                        />
                        
                        {/* Angle arc */}
                        <path 
                          d={`M 100 300 A 50 50 0 0 0 ${50 + 50 * Math.cos(planeAngle * Math.PI / 180)} ${300 - 50 * Math.sin(planeAngle * Math.PI / 180)}`}
                          stroke="#fbbf24" 
                          strokeWidth="2" 
                          fill="none"
                        />
                        <text x="120" y="285" fill="#fbbf24" fontSize="12">{planeAngle}°</text>
                      </g>
                      
                      {/* Object on incline */}
                      <g transform={`translate(${70 + objectPosition * 2}, ${280 - objectPosition * 2 * Math.tan(planeAngle * Math.PI / 180)}) rotate(${-planeAngle})`}>
                        <rect x="-20" y="-40" width="40" height="40" rx="5" fill="#ec4899"/>
                        <text x="0" y="-15" textAnchor="middle" fill="white" fontSize="10">{objectMass}kg</text>
                        
                        {/* Force arrows */}
                        {/* Weight (down) */}
                        <line x1="0" y1="0" x2="0" y2="40" stroke="#ef4444" strokeWidth="2"/>
                        <polygon points="0,40 -5,30 5,30" fill="#ef4444"/>
                        
                        {/* Normal force */}
                        <line x1="0" y1="0" x2="0" y2="-30" stroke="#22c55e" strokeWidth="2"/>
                        <polygon points="0,-30 -5,-20 5,-20" fill="#22c55e"/>
                      </g>
                      
                      {/* Height indicator */}
                      <line x1="50" y1="300" x2="50" y2={300 - 300 * Math.tan(planeAngle * Math.PI / 180)} stroke="#06b6d4" strokeWidth="2" strokeDasharray="5,5"/>
                      <text x="30" y={300 - 150 * Math.tan(planeAngle * Math.PI / 180)} fill="#06b6d4" fontSize="10" transform="rotate(-90, 30, 200)">Height</text>
                      
                      {/* Labels */}
                      <text x="250" y="330" textAnchor="middle" fill="#9ca3af" fontSize="12">
                        Force needed to push up: {forceNeeded} N (MA = {mechanicalAdvantage})
                      </text>
                    </svg>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Status */}
            <Card className={`border-2 backdrop-blur-xl transition-all ${
              currentExperiment === 'lever' 
                ? (isBalanced ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/30')
                : 'bg-teal-500/10 border-teal-500/30'
            }`}>
              <CardContent className="p-4 text-center">
                {currentExperiment === 'lever' && (
                  <>
                    <Scale className={`w-10 h-10 mx-auto mb-2 ${isBalanced ? 'text-green-400' : 'text-red-400'}`} />
                    <p className={`font-bold ${isBalanced ? 'text-green-400' : 'text-red-400'}`}>
                      {isBalanced ? '⚖️ Balanced!' : '❌ Not Balanced'}
                    </p>
                    <p className="text-white/60 text-sm mt-1">
                      {isBalanced ? 'Moments are equal!' : 'Adjust weights or distances'}
                    </p>
                  </>
                )}
                {currentExperiment === 'pulley' && (
                  <>
                    <Cog className="w-10 h-10 mx-auto mb-2 text-teal-400" />
                    <p className="text-teal-400 font-bold">
                      {pulleyType === 'single' ? 'Single Pulley' : pulleyType === 'double' ? 'Double Pulley' : 'Block & Tackle'}
                    </p>
                    <p className="text-white text-xl font-bold mt-2">
                      Effort: {effortNeeded.toFixed(0)} kg
                    </p>
                  </>
                )}
                {currentExperiment === 'incline' && (
                  <>
                    <Triangle className="w-10 h-10 mx-auto mb-2 text-cyan-400" />
                    <p className="text-cyan-400 font-bold">Inclined Plane</p>
                    <p className="text-white text-xl font-bold mt-2">
                      MA: {mechanicalAdvantage}x
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Theory */}
            <Card className="bg-black/40 border-white/10 backdrop-blur-xl">
              <CardContent className="p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-purple-400" />
                  Kya seekha?
                </h4>
                <div className="text-sm text-white/70 space-y-2">
                  {currentExperiment === 'lever' && (
                    <>
                      <p>⚖️ <span className="text-purple-300">Lever Principle:</span></p>
                      <p>Load × Load Distance = Effort × Effort Distance</p>
                      <p>📌 Fulcrum se door = kam force chahiye</p>
                      <p>📌 Seesaw, scissors, bottle opener - sab levers hain!</p>
                    </>
                  )}
                  {currentExperiment === 'pulley' && (
                    <>
                      <p>🔄 <span className="text-purple-300">Pulley Magic:</span></p>
                      <p>📌 Single: Direction change, no MA</p>
                      <p>📌 Double: 2x easier!</p>
                      <p>📌 Block: 4x easier!</p>
                      <p>📌 Trade-off: More rope pull distance</p>
                    </>
                  )}
                  {currentExperiment === 'incline' && (
                    <>
                      <p>📐 <span className="text-cyan-300">Inclined Plane:</span></p>
                      <p>MA = Length / Height</p>
                      <p>📌 Gentle slope = Less force needed</p>
                      <p>📌 Steep slope = More force needed</p>
                      <p>📌 Ramps, stairs, screws - sab inclined planes!</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Formula Card */}
            <Card className="bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border-teal-500/30 backdrop-blur-xl">
              <CardContent className="p-4 text-center">
                <p className="text-teal-300 text-xs mb-2">Key Formula</p>
                {currentExperiment === 'lever' && (
                  <p className="text-white font-mono text-lg">F₁ × d₁ = F₂ × d₂</p>
                )}
                {currentExperiment === 'pulley' && (
                  <p className="text-white font-mono text-lg">MA = Load / Effort</p>
                )}
                {currentExperiment === 'incline' && (
                  <p className="text-white font-mono text-lg">MA = L / h</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleMachinesPage;
